import type * as React from 'react'
import { LogInIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

// Presentational shell for the DevLoginAs feature: DevLoginAs wires up state/handlers and renders
// this for the live markup. Kept as a separate component so the disabled/in-flight states share one
// source of truth for the markup and can never drift apart.
export function DevLoginAsShell(props: {
  label: string
  error?: string | null
  // While the switch is in flight (login-as call + cache refresh + navigation) the button shows a
  // spinner so the whole transition has visible progress, not just the brief network call.
  loading?: boolean
  inputProps?: React.ComponentProps<typeof Input>
  buttonProps?: React.ComponentProps<typeof Button>
  children?: React.ReactNode
}) {
  return (
    // stopPropagation keeps the dropdown menu's typeahead/keyboard nav from stealing keystrokes while
    // typing in the input (harmless for the disabled fallback).
    <div className="flex flex-col gap-2 px-2 py-2" onKeyDown={(event) => event.stopPropagation()}>
      <span className="text-muted-foreground text-xs font-medium">{props.label}</span>
      <div className="flex items-center gap-2">
        <Input type="email" className="h-8" {...props.inputProps} />
        {props.children}
        <Button type="button" size="icon" variant="secondary" className="h-8 w-8 shrink-0" {...props.buttonProps}>
          {props.loading ? <Spinner className="h-4 w-4" /> : <LogInIcon className="h-4 w-4" />}
        </Button>
      </div>
      {props.error && <span className="text-destructive text-xs">{props.error}</span>}
    </div>
  )
}
